import { useState } from 'react';
import {
  Users,
  Plus,
  Settings,
  Trash2,
  Crown,
  Shield,
  Eye,
  ClipboardCheck,
  MoreVertical,
  Pencil,
  FileSearch,
  Loader2,
  FolderOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/authStore';
import {
  useTeams,
  useTeam,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  useRemoveMember,
  useUpdateMemberRole,
} from '@/features/team/hooks/useTeam';
import type { TeamMember, TeamRole } from '@/features/review/types';
import { useReviews, useCreateReview, useReviewerStats, useReviewSources, useAddSourcesToReview } from '@/features/review/hooks/useReview';
import { useSources } from '@/features/sources/hooks/useSources';
import { useFolderTree } from '@/features/sources/hooks/useFolders';
import { BlindToggle } from '@/features/review/components/BlindToggle';
import { ReviewerPanel } from '@/features/review/components/ReviewerPanel';
import { ConflictResolver } from '@/features/review/components/ConflictResolver';

// =========================================
// Role helpers
// =========================================

const ROLE_CONFIG: Record<
  TeamRole,
  { label: string; icon: typeof Crown; color: string }
> = {
  owner: { label: 'Owner', icon: Crown, color: 'text-amber-600 bg-amber-50' },
  admin: { label: 'Admin', icon: Shield, color: 'text-indigo-600 bg-indigo-50' },
  reviewer: {
    label: 'Reviewer',
    icon: ClipboardCheck,
    color: 'text-emerald-600 bg-emerald-50',
  },
  viewer: { label: 'Viewer', icon: Eye, color: 'text-slate-600 bg-slate-50' },
};

// =========================================
// Props
// =========================================

interface TeamManagerProps {
  selectedTeamId: string | null;
  onSelectTeam: (teamId: string | null) => void;
  onInvite: (teamId: string) => void;
}

// =========================================
// TeamManager Component
// =========================================

export function TeamManager({
  selectedTeamId,
  onSelectTeam,
  onInvite,
}: TeamManagerProps) {
  const user = useAuthStore((s) => s.user);
  const { data: teams, isLoading: teamsLoading } = useTeams();
  const { data: selectedTeam, isLoading: teamLoading } =
    useTeam(selectedTeamId ?? undefined);

  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();
  const removeMember = useRemoveMember();
  const updateRole = useUpdateMemberRole();

  const { data: reviews, isLoading: reviewsLoading } = useReviews(selectedTeamId ?? undefined);
  const createReview = useCreateReview();
  const addSourcesToReview = useAddSourcesToReview();
  const { data: folderTree } = useFolderTree();

  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);

  // Fetch sources assigned to the selected review (not all owner sources)
  const { data: reviewSources } = useReviewSources(selectedReviewId ?? undefined);
  const reviewSourceItems = (reviewSources ?? []).map((rs) => ({
    id: rs.source_id,
    title: rs.source?.title ?? null,
    file_name: rs.source?.file_name ?? 'Unknown',
  }));

  const { data: reviewerStats } = useReviewerStats(selectedReviewId ?? undefined);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState<TeamMember | null>(
    null
  );
  const [showCreateReviewDialog, setShowCreateReviewDialog] = useState(false);
  const [newReviewName, setNewReviewName] = useState('');
  const [newReviewBlinded, setNewReviewBlinded] = useState(true);
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set());
  const [sourceSearch, setSourceSearch] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // Fetch sources filtered by folder (or all if no folder selected)
  const { data: userSources } = useSources(
    selectedFolderId ? { folder_id: selectedFolderId } : {}
  );
  const [newTeamName, setNewTeamName] = useState('');
  const [renameName, setRenameName] = useState('');

  // ---- Handlers ----

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const team = await createTeam.mutateAsync(newTeamName.trim());
      setShowCreateDialog(false);
      setNewTeamName('');
      onSelectTeam(team.id);
      toast.success('Team created successfully');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create team'
      );
    }
  };

  const handleRenameTeam = async () => {
    if (!selectedTeamId || !renameName.trim()) return;
    try {
      await updateTeam.mutateAsync({
        teamId: selectedTeamId,
        name: renameName.trim(),
      });
      setShowRenameDialog(false);
      toast.success('Team renamed');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to rename team'
      );
    }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeamId) return;
    try {
      await deleteTeam.mutateAsync(selectedTeamId);
      setShowDeleteDialog(false);
      onSelectTeam(null);
      toast.success('Team deleted');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to delete team'
      );
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    try {
      await removeMember.mutateAsync({
        memberId: member.id,
        teamId: member.team_id,
      });
      setShowRemoveDialog(null);
      toast.success('Member removed');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to remove member'
      );
    }
  };

  const handleChangeRole = async (member: TeamMember, role: TeamRole) => {
    try {
      await updateRole.mutateAsync({
        memberId: member.id,
        teamId: member.team_id,
        role,
      });
      toast.success(`Role updated to ${ROLE_CONFIG[role].label}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update role'
      );
    }
  };

  const handleCreateReview = async () => {
    if (!selectedTeamId || !newReviewName.trim()) return;
    try {
      const review = await createReview.mutateAsync({
        team_id: selectedTeamId,
        name: newReviewName.trim(),
        blinded: newReviewBlinded,
      });
      // Add selected sources to the review
      if (selectedSourceIds.size > 0) {
        await addSourcesToReview.mutateAsync({
          reviewId: review.id,
          sourceIds: Array.from(selectedSourceIds),
        });
      }
      setShowCreateReviewDialog(false);
      setNewReviewName('');
      setNewReviewBlinded(true);
      setSelectedSourceIds(new Set());
      setSourceSearch('');
      setSelectedFolderId(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create review'
      );
    }
  };

  const isOwner = selectedTeam?.owner_id === user?.id;
  const isAdmin =
    isOwner ||
    selectedTeam?.members.some(
      (m) =>
        m.user_id === user?.id &&
        m.role === 'admin' &&
        m.accepted_at !== null
    );

  // ---- Render ----

  return (
    <div className="space-y-6">
      {/* Team list + create button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Your Teams</h2>
        <Button size="sm" onClick={() => setShowCreateDialog(true)}>
          <Plus className="mr-1 h-4 w-4" />
          New Team
        </Button>
      </div>

      {/* Teams grid */}
      {teamsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card
              key={i}
              className="animate-pulse h-24 bg-slate-100 border-none"
            />
          ))}
        </div>
      ) : !teams?.length ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Users className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No teams yet</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => setShowCreateDialog(true)}
            >
              Create your first team
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((team) => (
            <Card
              key={team.id}
              className={cn(
                'cursor-pointer transition-all hover:shadow-md',
                selectedTeamId === team.id &&
                  'ring-2 ring-indigo-500 shadow-md'
              )}
              onClick={() => onSelectTeam(team.id)}
            >
              <CardContent className="flex items-center justify-between py-4 px-5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100">
                    <Users className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-slate-900 truncate">
                      {team.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {team.member_count}{' '}
                      {team.member_count === 1 ? 'member' : 'members'}
                    </p>
                  </div>
                </div>
                {team.owner_id === user?.id && (
                  <Badge variant="secondary" className="text-xs shrink-0">
                    Owner
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Selected team detail */}
      {selectedTeamId && selectedTeam && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">{selectedTeam.name}</CardTitle>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onInvite(selectedTeamId)}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Invite
                </Button>
              )}
              {isOwner && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-8 w-8">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        setRenameName(selectedTeam.name);
                        setShowRenameDialog(true);
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-rose-600"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Team
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </CardHeader>

          <CardContent>
            {teamLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-12 animate-pulse bg-slate-100 rounded-lg"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {selectedTeam.members.map((member) => {
                  const roleConfig = ROLE_CONFIG[member.role];
                  const RoleIcon = roleConfig.icon;
                  const isSelf = member.user_id === user?.id;
                  const isPending = !member.accepted_at;

                  return (
                    <div
                      key={member.id}
                      className={cn(
                        'flex items-center justify-between rounded-lg border p-3',
                        isPending && 'bg-slate-50 border-dashed'
                      )}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-sm font-medium text-slate-600">
                          {(
                            member.profile?.full_name?.[0] ??
                            member.profile?.email?.[0] ??
                            '?'
                          ).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {member.profile?.full_name ||
                              member.profile?.email ||
                              'Unknown'}
                            {isSelf && (
                              <span className="ml-1 text-xs text-slate-400">
                                (you)
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {member.profile?.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {isPending && (
                          <Badge
                            variant="outline"
                            className="text-xs text-amber-600 border-amber-200"
                          >
                            Pending
                          </Badge>
                        )}
                        <Tooltip>
                          <TooltipTrigger>
                            <Badge
                              variant="secondary"
                              className={cn(
                                'text-xs gap-1',
                                roleConfig.color
                              )}
                            >
                              <RoleIcon className="h-3 w-3" />
                              {roleConfig.label}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            {roleConfig.label} role
                          </TooltipContent>
                        </Tooltip>

                        {isAdmin &&
                          !isSelf &&
                          member.role !== 'owner' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7"
                                >
                                  <MoreVertical className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {(['admin', 'reviewer', 'viewer'] as const).map(
                                  (r) =>
                                    r !== member.role && (
                                      <DropdownMenuItem
                                        key={r}
                                        onClick={() =>
                                          handleChangeRole(member, r)
                                        }
                                      >
                                        Set as {ROLE_CONFIG[r].label}
                                      </DropdownMenuItem>
                                    )
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-rose-600"
                                  onClick={() =>
                                    setShowRemoveDialog(member)
                                  }
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                      </div>
                    </div>
                  );
                })}

                {selectedTeam.members.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No members yet. Invite someone to get started.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reviews section for selected team */}
      {selectedTeamId && selectedTeam && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-indigo-600" />
              Reviews
            </CardTitle>
            {isAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCreateReviewDialog(true)}
              >
                <Plus className="mr-1 h-4 w-4" />
                New Review
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {reviewsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : !reviews?.length ? (
              <div className="text-center py-6">
                <FileSearch className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No reviews yet</p>
                <p className="text-xs text-slate-400 mt-1">
                  Create a review to start screening sources with your team
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {reviews.map((review) => {
                  const isSelected = selectedReviewId === review.id;
                  return (
                    <div
                      key={review.id}
                      className={cn(
                        'rounded-lg border p-4 space-y-3 cursor-pointer transition-all',
                        isSelected && 'ring-2 ring-indigo-500 shadow-md'
                      )}
                      onClick={() => setSelectedReviewId(isSelected ? null : review.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {review.name}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Status: {review.status} &middot; Created{' '}
                            {new Date(review.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge
                          variant="secondary"
                          className={cn(
                            'text-xs',
                            review.status === 'screening' && 'bg-amber-50 text-amber-700',
                            review.status === 'completed' && 'bg-emerald-50 text-emerald-700',
                          )}
                        >
                          {review.status}
                        </Badge>
                      </div>
                      <BlindToggle
                        reviewId={review.id}
                        blinded={review.blinded}
                        disabled={!isAdmin}
                      />

                      {/* Expanded review detail */}
                      {isSelected && (
                        <div className="space-y-6 pt-3 border-t" onClick={(e) => e.stopPropagation()}>
                          {/* Reviewer Panel — screening decisions */}
                          <ReviewerPanel
                            review={review}
                            sources={reviewSourceItems}
                            phase={review.status === 'full_text' ? 'full_text' : 'screening'}
                          />

                          {/* Conflict Resolver */}
                          <ConflictResolver
                            reviewId={review.id}
                            phase={review.status === 'full_text' ? 'full_text' : 'screening'}
                            canResolve={isAdmin ?? false}
                          />

                          {/* Reviewer Stats */}
                          {reviewerStats && reviewerStats.length > 0 && (
                            <Card>
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                  <Users className="h-4 w-4 text-indigo-600" />
                                  Reviewer Statistics
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3">
                                  {reviewerStats.map((rs) => (
                                    <div
                                      key={rs.reviewer_id}
                                      className="flex items-center justify-between rounded-lg border p-3"
                                    >
                                      <div>
                                        <p className="text-sm font-medium text-slate-900">
                                          {rs.reviewer_name}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                          {rs.decisions_count} decisions · {rs.include_count} include · {rs.exclude_count} exclude · {rs.maybe_count} maybe
                                        </p>
                                      </div>
                                      <Badge
                                        variant="secondary"
                                        className={cn(
                                          'text-xs',
                                          rs.agreement_rate >= 0.7 && 'bg-emerald-50 text-emerald-700',
                                          rs.agreement_rate >= 0.4 && rs.agreement_rate < 0.7 && 'bg-amber-50 text-amber-700',
                                          rs.agreement_rate < 0.4 && 'bg-rose-50 text-rose-700',
                                        )}
                                      >
                                        {Math.round(rs.agreement_rate * 100)}% agreement
                                      </Badge>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Review Dialog */}
      <Dialog open={showCreateReviewDialog} onOpenChange={(open) => {
        setShowCreateReviewDialog(open);
        if (!open) {
          setSelectedSourceIds(new Set());
          setSourceSearch('');
          setSelectedFolderId(null);
        }
      }}>
        <DialogContent className="max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Create Review</DialogTitle>
            <DialogDescription>
              Start a new screening review for your team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 overflow-y-auto min-h-0">
            <div className="space-y-2">
              <Label htmlFor="review-name">Review Name</Label>
              <Input
                id="review-name"
                placeholder="e.g., Screening Round 2"
                value={newReviewName}
                onChange={(e) => setNewReviewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateReview()}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="review-blinded" className="text-sm font-medium cursor-pointer">
                  Blinded Review
                </Label>
                <p className="text-xs text-slate-500">
                  Reviewers cannot see each other&apos;s decisions
                </p>
              </div>
              <input
                id="review-blinded"
                type="checkbox"
                checked={newReviewBlinded}
                onChange={(e) => setNewReviewBlinded(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
            </div>

            {/* Source picker */}
            <div className="space-y-2 min-h-0 flex flex-col">
              <div className="flex items-center justify-between">
                <Label>Sources to review</Label>
                <span className="text-xs text-slate-500">
                  {selectedSourceIds.size} selected
                </span>
              </div>

              {/* Folder filter tabs */}
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setSelectedFolderId(null)}
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                    selectedFolderId === null
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  )}
                >
                  All sources
                </button>
                {(folderTree ?? []).map((folder) => (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => setSelectedFolderId(folder.id)}
                    className={cn(
                      'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                      selectedFolderId === folder.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    )}
                  >
                    <FolderOpen className="h-3 w-3" />
                    {folder.name}
                    <span className="text-[10px] opacity-60">({folder.source_count})</span>
                  </button>
                ))}
              </div>

              <Input
                placeholder="Search sources..."
                value={sourceSearch}
                onChange={(e) => setSourceSearch(e.target.value)}
                className="h-8 text-sm"
              />
              <div className="border rounded-lg min-h-0 max-h-40 overflow-y-auto flex-shrink">
                {(() => {
                  const available = (userSources ?? []).filter(
                    (s) => !s.is_duplicate
                  );
                  const filtered = sourceSearch
                    ? available.filter(
                        (s) =>
                          (s.title ?? '').toLowerCase().includes(sourceSearch.toLowerCase()) ||
                          s.file_name.toLowerCase().includes(sourceSearch.toLowerCase())
                      )
                    : available;

                  if (available.length === 0) {
                    return (
                      <p className="text-xs text-slate-400 text-center py-4">
                        {selectedFolderId ? 'No sources in this folder.' : 'No sources available. Import sources first.'}
                      </p>
                    );
                  }

                  return (
                    <>
                      {filtered.length > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-slate-50 sticky top-0">
                          <input
                            type="checkbox"
                            checked={filtered.every((s) => selectedSourceIds.has(s.id))}
                            onChange={(e) => {
                              setSelectedSourceIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) {
                                  filtered.forEach((s) => next.add(s.id));
                                } else {
                                  filtered.forEach((s) => next.delete(s.id));
                                }
                                return next;
                              });
                            }}
                            className="h-3.5 w-3.5 rounded border-slate-300"
                          />
                          <span className="text-xs text-slate-600 font-medium">
                            Select all ({filtered.length})
                          </span>
                        </div>
                      )}
                      {filtered.map((source) => (
                        <label
                          key={source.id}
                          className="flex items-start gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedSourceIds.has(source.id)}
                            onChange={(e) => {
                              setSelectedSourceIds((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(source.id);
                                else next.delete(source.id);
                                return next;
                              });
                            }}
                            className="h-3.5 w-3.5 mt-0.5 rounded border-slate-300 shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="text-sm text-slate-900 truncate">
                              {source.title ?? source.file_name}
                            </p>
                            {source.title && (
                              <p className="text-xs text-slate-400 truncate">
                                {source.file_name}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                      {filtered.length === 0 && (
                        <p className="text-xs text-slate-400 text-center py-3">
                          No matching sources
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateReviewDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateReview}
              disabled={!newReviewName.trim() || selectedSourceIds.size === 0 || createReview.isPending || addSourcesToReview.isPending}
            >
              {createReview.isPending || addSourcesToReview.isPending ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                `Create Review (${selectedSourceIds.size} sources)`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Team</DialogTitle>
            <DialogDescription>
              Create a new team to collaborate on reviews.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="team-name">Team Name</Label>
            <Input
              id="team-name"
              placeholder="e.g., Systematic Review Group"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTeam}
              disabled={!newTeamName.trim() || createTeam.isPending}
            >
              {createTeam.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="rename-name">Team Name</Label>
            <Input
              id="rename-name"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRenameTeam()}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRenameDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameTeam}
              disabled={!renameName.trim() || updateTeam.isPending}
            >
              {updateTeam.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              This will permanently delete the team and remove all members. This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTeam}
              disabled={deleteTeam.isPending}
            >
              {deleteTeam.isPending ? 'Deleting...' : 'Delete Team'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation */}
      <Dialog
        open={showRemoveDialog !== null}
        onOpenChange={(open) => !open && setShowRemoveDialog(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>
              Remove{' '}
              <span className="font-medium">
                {showRemoveDialog?.profile?.full_name ??
                  showRemoveDialog?.profile?.email ??
                  'this member'}
              </span>{' '}
              from the team?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRemoveDialog(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                showRemoveDialog && handleRemoveMember(showRemoveDialog)
              }
              disabled={removeMember.isPending}
            >
              {removeMember.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
